/**
 * The household and person this tab believes it is acting for (#105).
 *
 * The active household is server-side session state, and every tab of a
 * browser shares the session. When one tab switches household, another keeps
 * showing — and writing into — what it thinks is the old one until its live
 * stream notices, and the offline outbox can replay a write long after the
 * person who made it has signed out. The server cannot see any of that unless
 * the request says what it expected, so every API request now does, and every
 * queued write records the pair it was made under.
 *
 * Observation only. The server compares the expectation with what the session
 * resolved and logs a mismatch; it never refuses a request because of it, and
 * never treats these values as identity — the session is the only answer to
 * "who is this?". Like the write id (writeIdentity.ts), the shell publishes the
 * value and the api client reads it through this seam.
 */

/** Header names. Lower-case because `Headers` normalizes, and the API reads them that way. */
export const EXPECTED_HOUSEHOLD_HEADER = "x-expected-household";
export const EXPECTED_USER_HEADER = "x-expected-user";
/** Sent with value "1" on a write the offline outbox is replaying. */
export const OFFLINE_REPLAY_HEADER = "x-offline-replay";

export type ExpectedIdentity = {
  householdId: string | null;
  userId: string | null;
};

const signedOut: ExpectedIdentity = { householdId: null, userId: null };

let current: ExpectedIdentity = signedOut;

/** Called by the shell whenever it applies an auth status, and with null on sign-out. */
export function setExpectedIdentity(next: Partial<ExpectedIdentity> | null): void {
  current = next ? { householdId: next.householdId || null, userId: next.userId || null } : signedOut;
}

/** What this tab believes right now. */
export function expectedIdentity(): ExpectedIdentity {
  return current;
}

/** The headers naming an expectation; empty when nothing is known yet. */
export function expectedIdentityHeaders(identity: Partial<ExpectedIdentity> | null | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  if (identity?.householdId) headers[EXPECTED_HOUSEHOLD_HEADER] = identity.householdId;
  if (identity?.userId) headers[EXPECTED_USER_HEADER] = identity.userId;
  return headers;
}

/**
 * `init` with this tab's expectation added to its headers, for a same-origin API
 * call made with `fetch` directly rather than through `api()`: a streamed
 * assistant turn, a file download, the label sheet, the push subscription.
 * Without it those requests carry no expectation and the server cannot compare
 * them at all. A header the caller set itself wins.
 */
export function withExpectedIdentity(init: RequestInit = {}): RequestInit {
  const extra = expectedIdentityHeaders(expectedIdentity());
  if (Object.keys(extra).length === 0) return init;
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(extra)) {
    if (!headers.has(name)) headers.set(name, value);
  }
  return { ...init, headers };
}
